import ContentLoader from 'react-content-loader';
import styled, { useTheme } from 'styled-components';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive.js';

const DemoContainer = styled.div`
  width: 100%;
  height: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding-top: 20px;

  @media ${PHONE_MEDIA_QUERY} {
    justify-content: flex-start;
    gap: 12px;
  }
`;

const ContentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const TokenInfoContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 30px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const Line = styled.div`
  margin-top: 40px;
  margin-bottom: 40px;
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.border.primary};

  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 20px;
    margin-bottom: 20px;
  }
`;

const ChartContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    border: 0px;
    padding: 0px;
    gap: 8px;
  }
`;

const TokenSwapContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    border: 0px;
    padding: 0px;
    gap: 8px;
  }
`;

const TokenInfoSkeleton = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  return (
    <ContentLoader
      speed={2}
      width={270}
      height={isMobile ? 60 : 180}
      viewBox={`0 0 270 ${isMobile ? 60 : 180}`}
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
    >
      <rect
        x='0'
        y='0'
        rx='4'
        ry='4'
        width={isMobile ? '135' : '270'}
        height={isMobile ? '30' : '40'}
      />
      <rect
        x='0'
        y={isMobile ? '38' : '48'}
        rx='4'
        ry='4'
        width={isMobile ? '85' : '170'}
        height='16'
      />
      {!isMobile && (
        <>
          <rect
            x='0'
            y={isMobile ? '62' : '72'}
            rx='4'
            ry='4'
            width='130'
            height='16'
          />
          <rect x='0' y='96' rx='4' ry='4' width='270' height='36' />
          <rect x='0' y='140' rx='4' ry='4' width='200' height='22' />
        </>
      )}
    </ContentLoader>
  );
};

const BackSkeleton = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();

  const size = isMobile ? 60 : 149;
  const sizeX = isMobile ? 14 : 25;

  return (
    <ContentLoader
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
      speed={2}
      width={sizeX}
      height={size}
      viewBox={`0 0 ${sizeX} ${size}`}
    >
      <rect x='0' y='0' rx='0' ry='0' width={sizeX} height={size} />
    </ContentLoader>
  );
};

const ChartSkeleton = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();

  return (
    <ContentLoader
      speed={2}
      width='100%'
      height={isMobile ? 221 : 300}
      viewBox={`0 0 100% ${isMobile ? 221 : 300}`}
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
    >
      <rect
        x='0'
        y='0'
        rx={isMobile ? '6' : '16'}
        ry={isMobile ? '6' : '16'}
        width='100%'
        height={isMobile ? 221 : 300}
      />
    </ContentLoader>
  );
};

const CircleSkeleton = () => {
  const theme = useTheme();
  return (
    <ContentLoader
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
      speed={2}
      width={58}
      height={58}
      viewBox='0 0 58 58'
    >
      <rect x='0' y='0' rx='58' ry='58' width='58' height='58' />
    </ContentLoader>
  );
};

const SwapSkeleton = () => {
  const theme = useTheme();
  return (
    <ContentLoader
      speed={2}
      width='100%'
      height={400}
      viewBox='0 0 600 400'
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
    >
      <rect x='0' y='0' rx='8' ry='8' width='120' height='40' />
      <rect x='0' y='60' rx='8' ry='8' width='100%' height='80' />
      <rect x='0' y='160' rx='8' ry='8' width='100%' height='80' />
      <rect x='0' y='260' rx='8' ry='8' width='100%' height='40' />
    </ContentLoader>
  );
};

const TokenLogoSkeleton = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  const size = isMobile ? 60 : 149;

  return (
    <ContentLoader
      speed={2}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
    >
      <rect x='0' y='0' rx='0' ry='0' width={size} height={size} />
    </ContentLoader>
  );
};

const CircleSkeletonRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const TabContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const TabButtonSkeleton = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  const size = isMobile ? 50 : 60;

  return (
    <ContentLoader
      speed={2}
      width={'100%'}
      height={size}
      viewBox={`0 0 100% ${size}`}
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
    >
      <rect x='0' y='0' rx='12' ry='12' width='100%' height={size} />
    </ContentLoader>
  );
};

const SmallText = () => {
  const theme = useTheme();
  return (
    <ContentLoader
      speed={2}
      width={'60'}
      height={30}
      viewBox='0 0 60 30'
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
    >
      <rect x='0' y='0' rx='4' ry='4' width='60' height='30' />
    </ContentLoader>
  );
};

const LongText = () => {
  const theme = useTheme();
  return (
    <ContentLoader
      speed={2}
      width={'90'}
      height={30}
      viewBox='0 0 90 30'
      backgroundColor={theme.background.secondary}
      foregroundColor={theme.special.skeleton}
    >
      <rect x='0' y='0' rx='4' ry='4' width='90' height='30' />
    </ContentLoader>
  );
};

const TotalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 16px;
`;

const TokenHomeSkeleton = () => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  return (
    <DemoContainer>
      <TokenInfoContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: isMobile ? '8px' : '16px',
          }}
        >
          <BackSkeleton />
          <TokenLogoSkeleton />
        </div>
        <TokenInfoSkeleton />
      </TokenInfoContainer>
      {!isMobile && <Line />}
      <ContentRow>
        {isMobile && (
          <>
            <TokenInfoSkeleton />
            <TabContainer>
              <TabButtonSkeleton />
              <TabButtonSkeleton />
            </TabContainer>
          </>
        )}
        <ChartContainer>
          <ChartSkeleton />
          <CircleSkeletonRow>
            <CircleSkeleton />
            <CircleSkeleton />
            <CircleSkeleton />
            <CircleSkeleton />
          </CircleSkeletonRow>
        </ChartContainer>
        <TokenSwapContainer>
          {!isMobile && (
            <>
              <TabContainer>
                <TabButtonSkeleton />
                <TabButtonSkeleton />
              </TabContainer>
            </>
          )}
          <TabContainer>
            <TabButtonSkeleton />
            <TabButtonSkeleton />
            <TabButtonSkeleton />
            <TabButtonSkeleton />
          </TabContainer>
          <TabContainer>
            <TabButtonSkeleton />
            <TabButtonSkeleton />
          </TabContainer>
          <TabContainer>
            <TabButtonSkeleton />
          </TabContainer>
          <TotalContainer>
            <SmallText />
            <LongText />
          </TotalContainer>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ContentLoader
              speed={2}
              width={120}
              height={48}
              viewBox='0 0 120 48'
              backgroundColor={theme.background.secondary}
              foregroundColor={theme.special.skeleton}
            >
              <rect x='0' y='0' rx='8' ry='8' width='120' height='48' />
            </ContentLoader>
          </div>
        </TokenSwapContainer>
      </ContentRow>
    </DemoContainer>
  );
};

export default TokenHomeSkeleton;
